import cloneDeep from 'lodash/cloneDeep'
import { router } from '@/router'
import { logger } from '@/logger/logger'

export const openLink = (link: string, useDefaultBrowser = false, target?: string): void => {
  try {
    // Flutter app handler
    if ((window as any)?.flutter_inappwebview?.callHandler) {
      (window as any)?.flutter_inappwebview?.callHandler('redirectToExternalLink', link, useDefaultBrowser)
      return
    }

    // Xeneon Edge iCUE widget handler - only use linkprovider when target is a new window
    if (isIcue()) {
      target === '_blank' ? getIcueLinkProvider().open(link) : window.location.href = link
      return
    }

    // Old iphone app handlers - deprecated and should be removed
    if ((window as any)?.webkit?.messageHandlers?.defaultUrlLoader?.postMessage && useDefaultBrowser) {
      (window as any).webkit.messageHandlers.defaultUrlLoader.postMessage(link)
      return
    }
    if ((window as any)?.webkit?.messageHandlers?.externalUrlLoader?.postMessage) {
      (window as any).webkit.messageHandlers.externalUrlLoader.postMessage(link)
      return
    }

    // Old Android app handler - deprecated and should be removed
    if ((window as any)?.Android?.redirectToExternalLink) {
      (window as any).Android.redirectToExternalLink(link, useDefaultBrowser)
      return
    }

    // If nothing else matches, it must be on web, open normally
    window.open(link, target)
  } catch (error) {
    logger.error("Open link error", error)
  }
}

export const updateObject = <T extends object>(firstObject: T, secondObject: Partial<T>): T => {
  return Object.assign({}, firstObject, secondObject)
}

interface Point {
  x: number
  y: number
}

export const getMaxX = (data: Point[]): number => {
  return data.reduce((max, p) => p.x > max ? p.x : max, data[0].x)
}

export const getMaxY = (data: Point[]): number => {
  return data.reduce((max, p) => p.y > max ? p.y : max, data[0].y)
}

export const zeroFill = (number: number | string, width: number): string => {
  width -= number.toString().length
  if (width > 0) {
    return new Array(width + (/\./.test(String(number)) ? 2 : 1)).join('0') + number
  }
  return number + ''
}

export const promiseWithTimeout = <T>(
  promise: Promise<T>,
  timeout = 1000,
  extraInfo: Record<string, any> = {}
): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      setTimeout(() => {
        let toLog = ''
        const keys = Object.keys(extraInfo)

        if (keys.length) {
          toLog = '\n' + keys.map(key => {
            return `${key}: ${extraInfo[key]}`
          }).join('\n')
        }

        reject(new Error(`Timed out after ${timeout}ms.${toLog}`))
      }, timeout)
    })
  ])
}

export const fetchWithTimeout = (url: string, options?: RequestInit, timeout?: number): Promise<Response> => {
  return promiseWithTimeout(
    fetch(url, options),
    timeout,
    {
      url
    }
  )
}

export const appendScript = (src: string, cleanup = false): Promise<Event> => {
  if (cleanup) {
    forEach(document.querySelectorAll(`[src="${src}"]`), (script) => {
      (script as Element).remove()
    })
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = src
    script.addEventListener('load', (e) => resolve(e))
    script.addEventListener('error', (e) => reject(e))
    document.head.appendChild(script)
  })
}


export const getGlobal = (): typeof globalThis =>
  typeof globalThis !== 'undefined' ? globalThis :
  typeof (global as any) !== 'undefined' ? (global as any) :
  typeof self !== 'undefined' ? self : window


export const commaSeparated = (value: number | string): string => {
  return Number(value).toLocaleString('en')
}

export const numberWithUnit = (number: any, unit?: string, notAnumber = 'N/A'): string => {
  return (typeof number === 'number') ? `${commaSeparated(number)}${unit ? ` ${unit}` : ''}` : notAnumber
}

interface QuantityAndUnit {
  quantity: number
  unit: string
}

export const getQuantityAndUnit = (value: string | number): QuantityAndUnit => {
  if (!(['string', 'number'].includes(typeof value))) {
    throw new Error('Must be a string or a number')
  }

  if (typeof value === 'number') {
    return {
      quantity: value,
      unit: 'px'
    }
  }

  const unit = value.replace(/[0-9.]+/gi, '')
  const quantity = Number(value.replace(unit, ''))

  return {
    quantity,
    unit
  }
}

export const objectFromArray = (value: any[] | object): Record<number, any> => {
  if (typeof value !== 'object') throw new Error('It must be an array or an object')

  if (Array.isArray(value)) {
    return value.reduce((object: Record<number, any>, el, i) => {
      object[i] = el
      return object
    }, {})
  } else {
    return value as Record<number, any>
  }
}

export const hash = (string: string): number => {
  if (typeof string !== 'string') {
    throw new Error('Argument must be a string!')
  }

  let hashVal = 0
  let i
  let char

  for (i = 0; i < string.length; i++) {
    char = string.charCodeAt(i)
    hashVal = ((hashVal << 5) - hashVal) + char
    hashVal |= 0
  }

  return hashVal
}

export const debounce = (func: (...args: any[]) => void, wait: number, immediate?: boolean) => {
  let timeout: ReturnType<typeof setTimeout> | null
  return (...args: any[]) => {
    const later = () => {
      timeout = null
      if (!immediate) func(...args)
    }
    const callNow = immediate && !timeout
    clearTimeout(timeout!)
    timeout = setTimeout(later, wait)
    if (callNow) func(...args)
  }
}

export const throttle = (func: (...args: any[]) => void, limit: number, finalCall?: boolean) => {
  let waiting = false
  let finalCallTimeout: ReturnType<typeof setTimeout> | null = null
  return (...args: any[]) => {
    finalCall && clearTimeout(finalCallTimeout!)
    if (!waiting) {
      func(...args)
      waiting = true
      setTimeout(() => {
        waiting = false
      }, limit)
    } else {
      finalCallTimeout = finalCall ? setTimeout(() => {
        func(...args)
      }, limit) : null
    }
  }
}

export const getNestedProp = (targetObject: any, path: string, defaultIfUndefined?: any): any => {
  let currentValue = targetObject

  try {
    /* eslint-disable */
    const keys = path.split(/[\[\].]+/).filter(_ => _)
    /* eslint-enable */

    keys.map((key) => {
      currentValue = currentValue[key] !== undefined ? currentValue[key] : defaultIfUndefined
    })
  } catch (_e) {
    currentValue = defaultIfUndefined
  }
  return currentValue
}

export const get = getNestedProp

export const setNestedProp = (targetObject: any, path: string, value: any, createNew = false): void => {
  /* eslint-disable */
  const keys = path.split(/[\[\].]+/).filter(_ => _)
  /* eslint-enable */

  keys.reduce((target: any, key: string, i: number) => {
    if (createNew && typeof target[key] === 'undefined' && i !== keys.length - 1) {
      target[key] = {}
      return target[key]
    }

    if (i === keys.length - 1) {
      target[key] = value
      return value
    } else {
      return target[key]
    }
  }, targetObject)
}

export const forEach = (array: any, callback: (item: any, index: number) => void): void => {
  if (!array || !callback) return
  for (let i = 0; i < array.length; i++) {
    callback(array[i], i)
  }
}

export const getCoordsFromEvent = (e: MouseEvent | TouchEvent | any): Point => {
  let coords: Point = {
    x: (e as MouseEvent).clientX || 0,
    y: (e as MouseEvent).clientY || 0
  }

  if (e.touches || e.targetTouches || e.changedTouches) {
    const touch = [e.touches, e.targetTouches, e.changedTouches]
      .filter((touches: any) => {
        return touches.length
      })[0][0]

    coords = {
      x: touch.clientX,
      y: touch.clientY
    }
  }

  return coords
}

export const findAncestor = (el: Element | null, cssSelector: string): Element | null => {
  while ((el = (el as Element).parentElement) && !(el.matches || (el as any).matchesSelector).call(el, cssSelector));
  return el
}

export const findAncestorWithStyle = (el: Element | null, cssProp: string, cssPropValue: string): Element | null => {
  if (!el || !cssProp || !cssPropValue) return null
  while ((el = (el as Element).parentElement) && !((getComputedStyle(el) as any)[cssProp] === cssPropValue));
  return el
}

export const findSibling = (el: Element, condition?: (el: Element) => boolean): Element | null => {
  const {
    nextElementSibling,
    nextSibling
  } = el
  const nextEl = nextElementSibling || (nextSibling as Element | null)

  if (!nextEl || !condition || typeof condition !== 'function') {
    return nextEl
  }

  if (condition(nextEl)) {
    return nextEl
  }

  return findSibling(nextEl, condition)
}

export const insertAfter = (target: Element, newElement: Element): void => {
  target.parentNode!.insertBefore(newElement, target.nextSibling)
}

export const isElementFullyInViewport = (el: Element): boolean => {
  try {
    const rect = el.getBoundingClientRect()

    return (
      rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    )
  } catch (_error) {
    return false
  }
}

export const getPaths = (pathname?: string): string[] | undefined => {
  pathname = pathname || location.pathname

  try {
    return pathname.split('/').filter(_ => _)
  } catch {
    // noop
  }
}

export const queryParams = (url = location.search): URLSearchParams => new URLSearchParams(url)
export const hasOwnProperty = (object: object, key: string): boolean => Object.prototype.hasOwnProperty.call(object, key)

export const buildQueryParams = (object: Record<string, any>): string => {
  const params: string[] = []

  try {
    for (const key in object) {
      if (hasOwnProperty(object, key)) {
        params.push(`${key}=${object[key]}`)
      }
    }
    return `?${params.join('&')}`
  } catch (_e) {
    return ''
  }
}

export class QueryParams {
  private _text?: string
  private _object?: URLSearchParams | Record<string, any>

  constructor(params: string | URLSearchParams | Record<string, any> | null = (typeof location !== 'undefined' ? location.search : null)) {
    switch (typeof params) {
    case 'string': {
      this.text = params
      break
    }

    case 'object': {
      if (params !== null) {
        this.object = params
      }
      break
    }

    default: break
    }
  }

  get text() { return this._text }

  set text(value: string | undefined) {
    this._text = buildQueryParams(queryParams(value))
    this._object = queryParams(value)
  }

  get object() { return this._object }

  set object(value: any) {
    this._object = value
    this._text = buildQueryParams(value)
  }

  set = (...args: any[]) => {
    if (typeof args[0] === 'object') {
      const params = args[0]
      this.object = params
      this.text = buildQueryParams(params)
    } else {
      const [name, value] = args
      ;(this._object as any)[name] = value
      this.text = buildQueryParams(this._object as any)
    }

    return this
  }

  remove = (name: string) => {
    const copyObject = { ...(this._object as any) }
    delete copyObject[name]
    this.object = copyObject
    this.text = buildQueryParams(this._object as any)
  }
}

export const stripHTML = (string: string): string => {
  if (document && document.createElement) {
    const el = document.createElement('div')
    el.innerHTML = string
    string = el.innerText
  }
  return string
}

export const getCookies = (name: string | null = null): string | Record<string, string> | undefined => {
  let cookies: Record<string, string> | undefined
  if (document.cookie && document.cookie !== '') {
    const cookieParts = document.cookie.split(';')
    const cookiesObject: Record<string, string> = {}
    for (let i = 0; i < cookieParts.length; i++) {
      const cookie = cookieParts[i].trim().split('=', 2)
      cookiesObject[cookie[0]] = cookie[1]
      if (name && name === cookie[0]) {
        break
      }
    }
    cookies = cookiesObject
  }
  return (name && cookies) ? cookies[name] : cookies
}

export const getCookie = getCookies

export const setCookie = (cookieName: string, cookieValue: string, expirationDays = 365): void => {
  const now = new Date()
  now.setTime(now.getTime() + expirationDays * 24 * 60 * 60 * 1000)
  const expires = `expires=${now.toUTCString()}`
  document.cookie = `${cookieName}=${cookieValue};${expires};Secure;path=/`
}

export const removeCookie = (cookieName: string): void => {
  document.cookie = cookieName + '=; Max-Age=-99999999;'
}

export const deepClone = <T>(object: T): T => cloneDeep(object)

export const wait = (milliseconds = 500, then: () => any = () => null): Promise<any> => {
  return (new Promise<void>(resolve => {
    setTimeout(() => {
      resolve()
    }, milliseconds)
  })).then(then)
}

export const toArray = (nodes: any): any[] => {
  try {
    return [].slice.call(nodes)
  } catch (_e) {
    return []
  }
}

export const findTallest = (elementsOrSelector: NodeList | Element[] | string): Element | null => {
  let tallest: Element | null = null
  const isElements = NodeList.prototype.isPrototypeOf.call(NodeList.prototype, elementsOrSelector) || typeof elementsOrSelector !== 'string'
  let elements: Element[] = isElements ? (elementsOrSelector as any) : Array.from(document.querySelectorAll(elementsOrSelector as string))
  elements = toArray(elements)

  if (!elements || !elements.length) return tallest

  const maxHeight = Math.max(...elements.map(el => (el as HTMLElement).offsetHeight))

  tallest = elements.filter(el => (el as HTMLElement).offsetHeight === maxHeight)[0]

  return tallest
}

export const findAvailableHeight = (window as any).findAvailableHeight = (el: HTMLElement): number => {
  if (!el) return 0
  const originalHeight = el.style.height
  el.style.height = '0'
  const parent = el.parentElement!
  const parentStyle = getComputedStyle(parent, null)
  const parentHeight = parent.clientHeight - parseFloat(parentStyle.getPropertyValue('padding-top')) - parseFloat(parentStyle.getPropertyValue('padding-bottom'))

  const siblingsHeight = Array
    .from(parent.children)
    .reduce((height: number, sibling: Element) => {
      if (sibling !== el) {
        const siblingStyle = getComputedStyle(sibling)

        if (!(['absolute', 'fixed'].includes(siblingStyle.position))) {
          return (height + ((sibling as HTMLElement).clientHeight + parseFloat(siblingStyle.getPropertyValue('margin-top')) + parseFloat(siblingStyle.getPropertyValue('margin-bottom'))))
        }
      }

      return height
    }, 0)

  el.style.height = originalHeight
  return (parentHeight - siblingsHeight)
}

export const getStackTrace = (): string | undefined => {
  const err = new Error()
  return err.stack
}

export const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

export const toCamelCase = (str: string): string => str.toLowerCase().replace(/\W+(.)/g, (_, char) => char.toUpperCase())

export const camelCaseToDashed = (str: string): string => str.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`)

export const camelToSnakeCase = (str: string): string => str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)

export const omniStorage = {
  prefix: 'OMNI_',
  prefixed(prefix = '') {
    return { ...this, prefix }
  },
  stores: {
    cookieStorage: (() => {
      if (!(window as any).cookieStorage) {
        Object.defineProperty(window, 'cookieStorage', {
          enumerable: true,
          get: () => ({
            getItem: getCookie,
            setItem: setCookie,
            removeItem: removeCookie,
          })
        })
      }

      return (window as any).cookieStorage
    })(),
    localStorage
  },
  shouldConvertValues: true,
  returnValue(value: any) {
    try {
      return this.shouldConvertValues ? JSON.parse(value) : value
    } catch (_e) {
      return value
    }
  },
  get(key: string) {
    if (typeof key === 'undefined') return
    let value
    const values: any[] = []

    for (const store in this.stores) {
      if ((this.stores as any)[store]) {
        const value = (this.stores as any)[store].getItem(this.prefix + key)

        if (value !== null && value !== undefined) {
          return this.returnValue(value)
        }
      }
    }

    const highestOccurrence = values.sort((a, b) => {
      return values.filter(v => v === a).length -
        values.filter(v => v === b).length
    }).pop()

    if (![highestOccurrence, typeof highestOccurrence].includes('undefined')) {
      if (values.some(v => v !== highestOccurrence)) {
        value = this.set(key, highestOccurrence)
      } else {
        value = highestOccurrence
      }

      return this.returnValue(value)
    } else {
      return undefined
    }
  },
  getItem(key: string) {
    return this.get(key)
  },
  set(key: string, value: any) {
    if ([typeof key].includes('undefined')) return
    if (this.shouldConvertValues && typeof value === 'object') {
      try {
        value = JSON.stringify(value)
      } catch (_e) {
        // ignore
      }
    }

    for (const store in this.stores) {
      if ((this.stores as any)[store]) {
        (this.stores as any)[store].setItem(this.prefix + key, value)
      }
    }

    return this.returnValue(value)
  },
  setItem(key: string, value: any) {
    return this.set(key, value)
  },
  remove(key: string) {
    if (typeof key === 'undefined') return
    for (const store in this.stores) {
      if ((this.stores as any)[store]) {
        (this.stores as any)[store].removeItem(this.prefix + key)
      }
    }

    return true
  },
  removeItem(key: string) {
    return this.remove(key)
  },
  allKeys() {
    let allKeys: string[] = []
    for (const store in this.stores) {
      if ((this.stores as any)[store]) {
        allKeys = allKeys.concat(
          Object.keys((this.stores as any)[store]).filter((key: string) => {
            return !key.indexOf(this.prefix)
          })
        )
      }
    }

    return allKeys.filter((key, i, array) => {
      return array.indexOf(key) === i
    })
  },
  getAll() {
    return this.allKeys().reduce((all: Record<string, any>, key: string) => {
      all[key] = this.get(key.replace(this.prefix, ''))
      return all
    }, {})
  },
  removeAll() {
    return this.allKeys().map((key: string) => {
      this.remove(key.replace(this.prefix, ''))
      return key
    })
  }
}

export const storage = omniStorage.prefixed('')

export const hexToRgb = (hex: string): string => {
  hex = hex.replace(/[^0-9A-F]/gi, '')

  const bigint = parseInt(hex, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255

  return `${r},${g},${b}`
}

export const rgbToHex = (rgb: string): string => {
  const [r, g, b] = rgb.split(',').map(Number)

  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)
}

export const isEquivalent = (a: Record<string, any>, b: Record<string, any>): boolean => {
  const aProps = Object.getOwnPropertyNames(a)
  const bProps = Object.getOwnPropertyNames(b)

  if (aProps.length !== bProps.length) {
    return false
  }

  for (let i = 0; i < aProps.length; i++) {
    const propName = aProps[i]

    if (a[propName] !== b[propName]) {
      return false
    }
  }

  return true
}

export const isApp = (): boolean => (!!(window as any)?.flutter_inappwebview?.callHandler || !!(window as any)?.webkit?.messageHandlers || !!(window as any)?.Android || localStorage.getItem('isApp') === 'true')

export const getIcueLinkProvider = () => (window as any)?.plugins?.Linkprovider || (window?.parent as any)?.plugins?.Linkprovider

export const isIcue = () => !!getIcueLinkProvider() || localStorage.getItem('source') === 'icue'

export const isMobile: boolean = (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(navigator.userAgent||navigator.vendor||(window as any).opera)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test((navigator.userAgent||navigator.vendor||(window as any).opera).substring(0,4)))

export const isIOS: boolean = /(iPad|iPhone|iPod)/g.test(navigator.userAgent)

interface AuthorizationOptions {
  ignoreStaff?: boolean
  redirect?: boolean | string
  matchEveryRole?: boolean
  staffOnly?: boolean
}

export function checkAuthorization(user: any, roles: string[], options: AuthorizationOptions = {
  ignoreStaff: false,
  redirect: false,
  matchEveryRole: false,
  staffOnly: false
}): boolean {
  const { ignoreStaff, redirect, matchEveryRole, staffOnly } = options

  let isAuthorized = (!ignoreStaff && user.is_staff) || (!staffOnly && user.glasspass && user.features.includes('glasspass') && roles.includes('glasspass'))

  if (!isAuthorized) {
    isAuthorized = roles[matchEveryRole ? 'every' : 'some']((role: string) => {
      return user && user.features.includes(role)
    })
  }

  !isAuthorized && redirect && router.navigate({ to: typeof redirect === 'string' ? redirect : '/shardlibrary', replace: true })

  return isAuthorized
}
